import { cmpStr, createCtx } from './ctx.js';
import { BUILTIN_TYPES, DRAFT } from './defaults.js';
import { rankBetween } from './narrative.js';
import { isScene } from './scene.js';
import { TIME_FIELDS } from './timeline.js';
import type { NodeType, Story, StoryNode, TextDocument } from './types.js';

/*
 * Übernahme älterer Daten in das Modell „Szene als einziges Objekt“ (PLAN.md §13). Rein und idempotent: liefert, was sich ändert,
 * oder null, wenn nichts zu tun ist. Server (Datenbank) und Projektordner wenden das Ergebnis an.
 *
 *  1. Szene, die ein Ereignis/einen Zeitraum zeigt (sonst: als Rück-/Vorausblende erzählt): beides wird eine Szene am Ort des
 *     Ereignisses (Lage, Personen darin, Verbindungen, Vorschaukarte). Felder der Szene gewinnen, leere füllen sich aus dem Ereignis;
 *     Texte des Ereignisses mit Inhalt bleiben als weitere Dokumente. Zeigen mehrere Szenen dasselbe, bekommen die weiteren dieselbe
 *     Zeit (Mehrfacherzählung).
 *  2. Ereignis/Zeitraum ohne Szene: wird eine Szene, nicht eingeordnet (mit Draft); hatte es schon einen Draft mit Text, kommt es in
 *     das erste Kapitel (wie früher die Übernahme der Drafts).
 *  3. Szene ohne Ereignis: bleibt im Kapitel, liegt nicht auf der Fläche (unter dem Manuskript).
 *  4. Elemente innerer Zeitleisten wandern auf den äußeren Zeitstrahl (Lage umgerechnet), die inneren Zeitleisten entfallen; Szenen in
 *     Szenen rücken auf die Fläche darüber.
 *  5. Die Klassen Ereignis und Zeitraum entfallen, die Klasse Szene bekommt ihre aktuellen Felder; Erzählt-Listen werden geleert.
 */

export interface SceneMigration {
  /** geänderte Nodes (vollständige Datensätze) */
  nodes: StoryNode[];
  /** entfernte Nodes: in ihr Ereignis gewanderte Szenen und innere Zeitleisten */
  removedNodes: string[];
  /** die Klasse Szene in ihrer aktuellen Form */
  sceneType: NodeType;
  /** die Klasse Szene gab es noch nicht */
  sceneTypeIsNew: boolean;
  /** entfernte Klassen (Ereignis, Zeitraum) */
  removedTypes: string[];
}

const SCENE_TEMPLATE = BUILTIN_TYPES.find((t) => t.key === 'scene')!;
const LEGACY_CATEGORIES = new Set<string>(['event', 'period']);
const isLegacyScene = (n: StoryNode): boolean => n.kind === 'scene';

/** Muss die Geschichte übernommen werden? */
export function needsSceneMigration(story: Story): boolean {
  const byId = new Map(story.nodes.map((n) => [n.id, n]));
  const sceneType = story.types.find((t) => t.builtin && t.category === 'scene');
  if (!sceneType || SCENE_TEMPLATE.fields.some((f) => !sceneType.fields.some((x) => x.key === f.key)) || sceneType.hoverKey !== SCENE_TEMPLATE.hoverKey || !sceneType.textual) return true;
  if (story.types.some((t) => LEGACY_CATEGORIES.has(t.category))) return true;
  return story.nodes.some((n) => {
    if (isLegacyScene(n) || (n.narrates?.length ?? 0) > 0) return true;
    const parent = n.parentId ? byId.get(n.parentId) : undefined;
    if (n.kind === 'timeline' && isScene(parent)) return true; // innere Zeitleiste
    if (isScene(n) && (isScene(parent) || n.typeId !== sceneType.id)) return true;
    return false;
  });
}

/** Szene über ihre Klasse prüfen (Felder der Klasse Szene; die früheren Zeitfelder bleiben für `migrateSceneTimes`, die sie übernimmt) */
const SCENE_KEYS = new Set([...SCENE_TEMPLATE.fields.map((f) => f.key), ...Object.values(TIME_FIELDS)]);
const sceneFields = (fields: Record<string, unknown>): StoryNode['fields'] => {
  const out: StoryNode['fields'] = {};
  for (const [k, v] of Object.entries(fields)) if (SCENE_KEYS.has(k) && (typeof v === 'string' || typeof v === 'number' || v === null)) out[k] = v;
  return out;
};

/** Felder der Szene gewinnen, leere füllen sich aus dem Ereignis. */
function mergeFields(event: StoryNode, scene: StoryNode): StoryNode['fields'] {
  const out = sceneFields(event.fields);
  for (const [k, v] of Object.entries(sceneFields(scene.fields))) {
    if (v === null || (typeof v === 'string' && v.trim() === '')) continue;
    out[k] = v;
  }
  return out;
}

export function migrateScenes(story: Story, newId: () => string): SceneMigration | null {
  if (!needsSceneMigration(story)) return null;
  const ctx = createCtx(story);
  const manuscript = story.nodes.find((n) => n.kind === 'manuscript');
  if (!manuscript) throw new Error('Das Manuskript fehlt (zuerst ensureManuscript).');

  // 5) Klasse Szene in aktueller Form (vordefinierte Klasse: Felder aus der Vorlage)
  const existing = story.types.find((t) => t.builtin && t.category === 'scene');
  const { key: _key, textual: _textual, ...tpl } = SCENE_TEMPLATE;
  const sceneType: NodeType = existing
    ? { ...existing, fields: tpl.fields, titleKey: tpl.titleKey, hoverKey: tpl.hoverKey, imageKey: tpl.imageKey, textual: true }
    : { ...tpl, id: newId(), textual: true, placeholderId: null };
  const legacyTypes = story.types.filter((t) => LEGACY_CATEGORIES.has(t.category));

  const changed = new Map<string, StoryNode>();
  const removed = new Set<string>();
  const get = (id: string | null | undefined): StoryNode | undefined => (id ? (changed.get(id) ?? ctx.nodes.get(id)) : undefined);
  const put = (n: StoryNode): void => void changed.set(n.id, n);
  const kidsOf = (id: string): StoryNode[] => story.nodes.map((n) => get(n.id)!).filter((n) => n.parentId === id && !removed.has(n.id));

  const draftDoc = (docs: TextDocument[]): TextDocument | undefined => docs.find((d) => d.name.toLowerCase() === DRAFT.toLowerCase());
  /** Draft (gesperrt) vorn; übrige Texte mit Inhalt bleiben (offene Dokumente), leere Standarddokumente entfallen */
  const sceneDocs = (own: TextDocument[], extra: TextDocument[] = []): TextDocument[] => {
    const draft = draftDoc(own) ?? draftDoc(extra.filter((d) => d.text.trim() !== ''));
    const out: TextDocument[] = [draft ? { ...draft, name: DRAFT, locked: true } : { id: newId(), name: DRAFT, text: '', locked: true }];
    const names = new Set([DRAFT.toLowerCase()]);
    for (const d of [...own, ...extra]) {
      if (d === draft || d.text.trim() === '') continue;
      let name = d.name.trim() || 'Notizen';
      for (let i = 2; names.has(name.toLowerCase()); i++) name = `${d.name} (${i})`;
      names.add(name.toLowerCase());
      out.push({ ...d, name, locked: false });
    }
    return out;
  };

  // 4a) innere Zeitleisten: Elemente auf den äußeren Zeitstrahl (linear auf die Spanne des Zeitraums umgerechnet)
  const inner = story.nodes.filter((n) => n.kind === 'timeline' && isScene(get(n.parentId)));
  for (const tl of inner) {
    for (const item of kidsOf(tl.id).filter(isScene)) {
      let from = item.kind === 'event' ? (item.at ?? 0) : (item.from ?? 0);
      let to = item.kind === 'event' ? from : (item.to ?? from);
      let cur: StoryNode = tl;
      let target: StoryNode | undefined;
      let loose: { parent: string; x: number; y: number } | null = null;
      for (let guard = 0; guard < 20; guard++) {
        const owner = get(cur.parentId);
        const t = cur.timeline;
        if (!owner || !t) break;
        const outer = get(owner.parentId);
        if (owner.kind !== 'period' || outer?.kind !== 'timeline') {
          // Zeitraum liegt lose (oder ist ein Zeitpunkt): das Element liegt dann lose daneben
          loose = { parent: owner.parentId ?? story.rootId, x: (owner.x ?? 0) + 60, y: (owner.y ?? 0) + 60 };
          break;
        }
        const span = (t.endPos - t.startPos) || 1;
        const scale = (p: number): number => (owner.from ?? 0) + ((p - t.startPos) / span) * ((owner.to ?? 0) - (owner.from ?? 0));
        from = scale(from);
        to = scale(to);
        cur = outer;
        if (!isScene(get(outer.parentId))) {
          target = outer;
          break;
        }
      }
      if (target) {
        put(item.kind === 'event' ? { ...item, parentId: target.id, at: Math.round(from), x: null, y: null } : { ...item, parentId: target.id, from: Math.round(from), to: Math.max(Math.round(to), Math.round(from) + 1), x: null, y: null });
      } else if (loose) {
        put({ ...item, parentId: loose.parent, x: loose.x, y: loose.y, at: null, from: null, to: null });
      }
    }
    removed.add(tl.id);
  }

  // Lesereihenfolge der früheren Szenen (Kapitel und Szenen nach Sortierschlüssel)
  const byRankThenId = (a: StoryNode, b: StoryNode): number => cmpStr(a.rank ?? '~', b.rank ?? '~') || cmpStr(a.createdAt, b.createdAt) || cmpStr(a.id, b.id);
  const chapters = story.nodes.filter((n) => n.kind === 'chapter' && n.parentId === manuscript.id).sort(byRankThenId);
  const chapterIds = new Set(chapters.map((c) => c.id));
  const legacyScenes = [
    ...chapters.flatMap((c) => story.nodes.filter((n) => isLegacyScene(n) && n.parentId === c.id).sort(byRankThenId)),
    ...story.nodes.filter((n) => isLegacyScene(n) && !chapterIds.has(n.parentId ?? '')),
  ];

  // 1) und 3) frühere Szenen
  const claimed = new Set<string>();
  for (const s of legacyScenes) {
    const narr = s.narrates ?? [];
    const pick = narr.find((e) => e.mode === 'shows') ?? narr.find((e) => e.mode === 'flashback' || e.mode === 'flashforward');
    const event = pick ? get(pick.nodeId) : undefined;
    const chapterId = s.parentId && chapterIds.has(s.parentId) ? s.parentId : null;
    const common = { typeId: sceneType.id, chapterId, rank: chapterId ? s.rank : null, narrates: [], narrateSkip: false };
    if (event && isScene(event) && !claimed.has(event.id) && !removed.has(event.id)) {
      claimed.add(event.id);
      put({
        ...event,
        ...common,
        fields: mergeFields(event, s),
        documents: sceneDocs(s.documents, event.documents),
        tagColor: event.tagColor ?? s.tagColor,
        dynamicFields: [...event.dynamicFields, ...s.dynamicFields],
      });
      removed.add(s.id);
    } else if (event && isScene(event)) {
      // Mehrfacherzählung: dieselbe Zeit wie die erste Szene dazu
      put({
        ...s,
        ...common,
        kind: event.kind,
        parentId: event.parentId,
        at: event.at,
        from: event.from,
        to: event.to,
        x: event.x === null ? null : event.x + 40,
        y: event.y === null ? null : event.y + 40,
        fields: mergeFields({ ...event, fields: { description: event.fields.description ?? null, image: event.fields.image ?? null } }, s),
        documents: sceneDocs(s.documents),
      });
    } else {
      put({ ...s, ...common, kind: 'event', parentId: manuscript.id, x: null, y: null, at: null, from: null, to: null, fields: sceneFields(s.fields), documents: sceneDocs(s.documents) });
    }
  }

  // 2) Ereignisse und Zeiträume ohne Szene; mit Draft (Text) ins erste Kapitel, in der Reihenfolge der Zeitstrahle
  const firstChapter = chapters[0];
  const usedRanks = story.nodes
    .map((n) => get(n.id)!)
    .filter((n) => firstChapter && n.chapterId === firstChapter.id && n.rank)
    .map((n) => n.rank!)
    .sort();
  let lastRank = usedRanks.at(-1) ?? null;
  const nextRank = (): string => {
    lastRank = rankBetween(lastRank, null);
    return lastRank;
  };
  const legacyIds = new Set(legacyTypes.map((t) => t.id));
  const timelineOrder = (n: StoryNode): [number, number] => {
    const parent = get(n.parentId);
    return parent?.kind === 'timeline' ? [parent.y ?? 0, n.kind === 'event' ? (n.at ?? 0) : (n.from ?? 0)] : [Number.MAX_SAFE_INTEGER, 0];
  };
  const rest = story.nodes
    .map((n) => get(n.id)!)
    .filter((n) => isScene(n) && !claimed.has(n.id) && !removed.has(n.id) && (legacyIds.has(n.typeId) || n.typeId !== sceneType.id))
    .sort((a, b) => {
      const [ay, ap] = timelineOrder(a);
      const [by, bp] = timelineOrder(b);
      return ay - by || ap - bp || cmpStr(a.createdAt, b.createdAt) || cmpStr(a.id, b.id);
    });
  for (const n of rest) {
    const written = (draftDoc(n.documents)?.text.trim() ?? '') !== '';
    const chapterId = written && firstChapter ? firstChapter.id : (n.chapterId ?? null);
    put({ ...n, typeId: sceneType.id, fields: sceneFields(n.fields), documents: sceneDocs(n.documents), chapterId, rank: chapterId ? (n.rank ?? nextRank()) : null, narrates: [] });
  }

  // 4b) Szenen in Szenen rücken auf die Fläche darüber (Zeitstrahle überspringen: dort wären sie angedockt)
  for (let guard = 0; guard < 50; guard++) {
    const nested = story.nodes.map((n) => get(n.id)!).filter((n) => !removed.has(n.id) && isScene(n) && isScene(get(n.parentId)));
    if (nested.length === 0) break;
    for (const n of nested) {
      let parent = get(n.parentId);
      let x = n.x ?? parent?.x ?? 0;
      let y = n.y ?? parent?.y ?? 0;
      while (parent && (isScene(parent) || parent.kind === 'timeline')) {
        x = parent.x ?? x;
        y = (parent.y ?? y) + 80;
        parent = get(parent.parentId);
      }
      put({ ...n, parentId: parent?.id ?? story.rootId, x: x + 40, y, at: null, from: null, to: null });
    }
  }

  // 5) Erzählt-Listen leeren (auch an anderen Nodes, falls vorhanden)
  for (const n of story.nodes) {
    const cur = get(n.id)!;
    if (!removed.has(n.id) && (cur.narrates?.length ?? 0) > 0) put({ ...cur, narrates: [] });
  }

  return {
    nodes: [...changed.values()].filter((n) => !removed.has(n.id)),
    removedNodes: [...removed],
    sceneType,
    sceneTypeIsNew: !existing,
    removedTypes: legacyTypes.map((t) => t.id),
  };
}

/** Wendet eine Übernahme auf eine Geschichte an (Projektordner, Tests). */
export function applySceneMigration(story: Story, m: SceneMigration): Story {
  const changed = new Map(m.nodes.map((n) => [n.id, n]));
  const removedNodes = new Set(m.removedNodes);
  const removedTypes = new Set(m.removedTypes);
  const types = story.types.filter((t) => !removedTypes.has(t.id) && t.id !== m.sceneType.id);
  return {
    ...story,
    types: [...types, m.sceneType].sort((a, b) => a.sort - b.sort),
    nodes: story.nodes.filter((n) => !removedNodes.has(n.id)).map((n) => changed.get(n.id) ?? n),
  };
}
